import {
  EuiBadge,
  EuiCallOut,
  EuiComboBox,
  EuiComboBoxOptionOption,
  EuiFlexGroup,
  EuiFlexItem,
  EuiForm,
  EuiFormRow,
  EuiPanel,
  EuiSpacer,
  EuiText,
} from "@elastic/eui";
import { useEffect, useMemo, useState } from "react";
import {
  AnketaResourcesEnum,
  KeycloakCreationRequest,
  KeycloakOrganizations,
  sanitizeOrganizationAlias,
  useAnketaI18n,
  useApi,
} from "../../../";
import { CreateNewOrganisation } from "./createNew";
import { useAnketaAuth } from "../../../auth/context";
import { SelectOrCreateOrganizationProps } from "../../../questionnaires/types";

const normalizeDomain = (domain: string): string =>
  domain.replace(/^@/, "").trim().toLowerCase();

export const SelectOrCreateOrganization = ({
  organizations,
  setOrganizations,
  newOrganizations,
  setNewOrganizations,
  setAllAvailableOptions,
  onConflictChange,
}: SelectOrCreateOrganizationProps & {
  onConflictChange?: (hasConflicts: boolean) => void;
}): JSX.Element => {
  const { Get } = useApi();
  const { translate } = useAnketaI18n();
  const { keycloak } = useAnketaAuth();
  const [isLoading, setIsLoading] = useState(false);

  const [options, setOptions] = useState<
    Array<EuiComboBoxOptionOption<KeycloakOrganizations>>
  >([]);

  const [allKnownDomains, setAllKnownDomains] = useState<Map<string, string>>(
    new Map(),
  );

  const [domainConflicts, setDomainConflicts] = useState<Map<string, string[]>>(
    new Map(),
  );

  const recomputeConflicts = (
    updatedNewOrgs: Array<EuiComboBoxOptionOption<KeycloakCreationRequest>>,
  ) => {
    const conflicts = new Map<string, string[]>();

    updatedNewOrgs.forEach((org) => {
      const orgDomains: string[] = org.value.domains ?? [];
      const orgConflicts: string[] = [];

      orgDomains.forEach((domain) => {
        const normalized = normalizeDomain(domain);

        if (allKnownDomains.has(normalized)) {
          const ownerName = allKnownDomains.get(normalized)!;
          orgConflicts.push(
            `@${normalized} (${translate("anketa.organizations.domain.takenBy") || "already used by"} "${ownerName}")`,
          );
          return;
        }

        const siblingConflict = updatedNewOrgs.find(
          (other) =>
            other.value.alias !== org.value.alias &&
            (other.value.domains ?? []).some(
              (d) => normalizeDomain(d) === normalized,
            ),
        );
        if (siblingConflict) {
          orgConflicts.push(
            `@${normalized} (${translate("anketa.organizations.domain.takenByNew") || "already used by new org"} "${siblingConflict.value.name}")`,
          );
        }
      });

      if (orgConflicts.length > 0) {
        conflicts.set(org.value.alias, orgConflicts);
      }
    });

    setDomainConflicts(conflicts);
  };

  const onChange = (
    selectedOptions: Array<EuiComboBoxOptionOption<KeycloakOrganizations>>,
  ) => {
    setOrganizations(selectedOptions);
  };

  const onCreateOption = (searchValue: string) => {
    const normalizedSearchValue = searchValue.trim().toLowerCase();
    if (!normalizedSearchValue) return;

    const value: KeycloakCreationRequest = {
      alias: sanitizeOrganizationAlias(searchValue),
      name: searchValue,
      domains: [],
    };
    const newOption = {
      label: searchValue,
      color: "danger",
      value,
    };
    setNewOrganizations((prevSelected) => [...prevSelected, newOption]);
  };

  const updateNewOrgs = (selectedOptions: KeycloakCreationRequest) => {
    setNewOrganizations((prevSelected) => {
      const exists = prevSelected.some(
        (opt) => opt.value.alias === selectedOptions.alias,
      );

      const updated = exists
        ? prevSelected.map((opt) =>
            opt.value.alias === selectedOptions.alias
              ? { ...opt, value: selectedOptions }
              : opt,
          )
        : [
            ...prevSelected,
            {
              value: selectedOptions,
              label: selectedOptions.name,
              color: "warning",
            },
          ];

      recomputeConflicts(updated);
      return updated;
    });
  };

  const removeNewOrg = (toRemove: KeycloakCreationRequest) => {
    setNewOrganizations((prevSelected) => {
      const updated = prevSelected.filter(
        (opt) => opt.value.alias !== toRemove.alias,
      );
      recomputeConflicts(updated);
      return updated;
    });
  };

  const newOrganizationRow = useMemo<Array<JSX.Element>>(() => {
    return newOrganizations.map((newOrganization) => {
      const conflicts = domainConflicts.get(newOrganization.value.alias) ?? [];
      return (
        <EuiFlexItem key={newOrganization.label}>
          {conflicts.length > 0 && (
            <>
              <EuiCallOut
                title={
                  translate("anketa.organizations.domain.conflict") ||
                  "Domain conflict detected"
                }
                color="danger"
                iconType="alert"
                size="s"
              >
                <ul style={{ margin: "4px 0 0", paddingLeft: 16 }}>
                  {conflicts.map((msg, i) => (
                    <li key={i}>
                      <EuiText size="xs">
                        <span>{msg}</span>
                      </EuiText>
                    </li>
                  ))}
                </ul>
              </EuiCallOut>
              <EuiSpacer size="s" />
            </>
          )}
          <CreateNewOrganisation
            name={newOrganization.label}
            updateNewOrgs={updateNewOrgs}
            removeNewOrg={removeNewOrg}
            orgDomains={
              newOrganization.value ? newOrganization.value.domains : []
            }
          />
        </EuiFlexItem>
      );
    });
  }, [newOrganizations, domainConflicts]);

  const existingDomainBadges = useMemo<Array<JSX.Element>>(() => {
    return organizations.flatMap(
      (option) =>
        option.value.domains?.map((domain, i) => (
          <EuiFlexItem
            key={`${option.value.alias}-${domain.name}-${i}`}
            grow={false}
          >
            <EuiBadge color="success">@{domain.name}</EuiBadge>
          </EuiFlexItem>
        )) ?? [],
    );
  }, [organizations]);

  const newOrganizationBadges = useMemo<Array<JSX.Element>>(() => {
    return newOrganizations.flatMap((option) => {
      if (option.value.domains) {
        return option.value.domains.map((domain, index) => {
          const normalized = normalizeDomain(domain);
          const isConflict =
            allKnownDomains.has(normalized) ||
            newOrganizations.some(
              (other) =>
                other.value.alias !== option.value.alias &&
                (other.value.domains ?? []).some(
                  (d) => normalizeDomain(d) === normalized,
                ),
            );
          return (
            <EuiFlexItem
              key={`${option.value.alias}-${domain}-${index}`}
              grow={false}
            >
              <EuiBadge color={isConflict ? "danger" : "warning"}>
                @{domain}
                {isConflict ? " ⚠" : ""}
              </EuiBadge>
            </EuiFlexItem>
          );
        });
      }
      return [];
    });
  }, [newOrganizations, allKnownDomains]);

  const allDomainBadges = [...existingDomainBadges, ...newOrganizationBadges];
  const hasDomains = allDomainBadges.length > 0;

  const hasDomainConflicts = domainConflicts.size > 0;

  useEffect(() => {
    onConflictChange?.(hasDomainConflicts);
  }, [hasDomainConflicts]);

  useEffect(() => {
    if (keycloak && keycloak.token) {
      setIsLoading(true);
      Get<Array<KeycloakOrganizations>>({
        path: `v2/${AnketaResourcesEnum.organizations}`,
        responseType: "json",
        token: keycloak.token,
      })
        .then((response) => {
          if (response.data) {
            const orgOptions = response.data.map((org) => ({
              label: org.name,
              value: org,
              id: org.id,
            }));
            setOptions(orgOptions);

            const domainMap = new Map<string, string>();
            response.data.forEach((org) => {
              org.domains?.forEach((d) => {
                domainMap.set(normalizeDomain(d.name), org.name);
              });
            });
            setAllKnownDomains(domainMap);
            const defaultOrgs = orgOptions.filter(
              (org) =>
                org.value.alias === "autonubil-gmbh" ||
                org.value.alias === "medocino-hamburg-gmbh",
            );
            onChange(defaultOrgs);
            if (setAllAvailableOptions) {
              setAllAvailableOptions(orgOptions);
            }
          }
        })
        .finally(() => setIsLoading(false));
    }
  }, [keycloak]);

  return (
    <EuiForm>
      <EuiFormRow
        label={translate("anketa.organizations")}
        helpText={
          options.length === 0 && !isLoading
            ? translate("anketa.organizations.empty") ||
              "No organizations found. Type a name to create one."
            : translate("anketa.organizations.helper") ||
              "Select existing or type to create a new organization"
        }
        fullWidth
      >
        <EuiComboBox
          placeholder={translate("anketa.create.selection", {
            values: { type: translate("anketa.organization") },
          })}
          options={options}
          selectedOptions={organizations}
          onChange={onChange}
          noSuggestions={false}
          onCreateOption={onCreateOption}
          isLoading={isLoading}
          fullWidth
        />
      </EuiFormRow>

      {hasDomains && (
        <>
          <EuiSpacer size="m" />
          <EuiFormRow
            label={
              <EuiText size="xs" color="subdued">
                <span>{translate("anketa.domains") || "Domains"}</span>
              </EuiText>
            }
            fullWidth
          >
            <EuiPanel color="subdued" paddingSize="s">
              <EuiFlexGroup wrap gutterSize="s">
                {allDomainBadges}
              </EuiFlexGroup>
            </EuiPanel>
          </EuiFormRow>
        </>
      )}

      {newOrganizationRow.length > 0 && (
        <>
          <EuiSpacer size="m" />
          <EuiCallOut
            title={
              translate("anketa.organizations.new.hint") ||
              "Configure domains for new organizations below"
            }
            color="warning"
            iconType="help"
            size="s"
          />
          <EuiSpacer size="s" />
          <EuiFormRow fullWidth>
            <EuiFlexGroup direction="column" gutterSize="m">
              {newOrganizationRow}
            </EuiFlexGroup>
          </EuiFormRow>
        </>
      )}
    </EuiForm>
  );
};
